import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, Image, Check, Loader2, X, Lock, Sparkles,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../lib/api';
import ProfileWidgetCanvas from './ProfileWidgetCanvas';
import IdentityCardWidget from './widgets/IdentityCardWidget';

/* Default CSS gradient wallpaper */
const DefaultWallpaper = () => (
  <div className="fixed inset-0 z-0" data-testid="default-wallpaper">
    <div className="absolute inset-0" style={{
      background: 'linear-gradient(135deg, #4AADA8 0%, #7BC4C1 25%, #A99CC8 50%, #C4B5D9 75%, #4AADA8 100%)',
    }} />
    {/* Islamic geometric pattern overlay */}
    <svg className="absolute inset-0 w-full h-full opacity-[0.06]" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="geo" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
          <path d="M40 0L80 40L40 80L0 40Z" fill="none" stroke="white" strokeWidth="1"/>
          <circle cx="40" cy="40" r="15" fill="none" stroke="white" strokeWidth="0.5"/>
          <path d="M20 20L60 20L60 60L20 60Z" fill="none" stroke="white" strokeWidth="0.5"/>
          <circle cx="40" cy="40" r="28" fill="none" stroke="white" strokeWidth="0.3"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#geo)" />
    </svg>
  </div>
);

const PublicProfilePage = ({ username, onBack, onNavigate }) => {
  const { profile: myProfile } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [followLoading, setFollowLoading] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [showWallpaperPicker, setShowWallpaperPicker] = useState(false);
  const [wallpapers, setWallpapers] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [widgetsLoading, setWidgetsLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getPublicProfile(username, myProfile?.id);
      setProfileData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Profile not available');
    }
    setLoading(false);
  }, [username, myProfile?.id]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const isEligible = !!(profileData && (
    profileData.is_premium || profileData.is_mentor || profileData.is_admin || profileData.is_moderator
  ));

  useEffect(() => {
    if (!profileData?.id || !isEligible) { setWidgetsLoading(false); return; }
    setWidgetsLoading(true);
    api.getProfileWidgets(profileData.id)
      .then(res => setWidgets(res.data || []))
      .catch(() => setWidgets([]))
      .finally(() => setWidgetsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData?.id, isEligible]);

  const persistWidgets = async (newWidgets) => {
    setWidgets(newWidgets); // optimistic — feels instant
    try {
      // Position/column_slot are already correct per-column here (the canvas
      // computes them on every reorder) — pass them through as-is.
      const res = await api.saveProfileWidgets(profileData.id, newWidgets.map((w) => ({
        id: w.id, widget_type: w.widget_type, position: w.position,
        column_slot: w.column_slot || 'center', config: w.config || {},
      })));
      setWidgets(res.data || newWidgets);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not save layout. Please try again.');
    }
  };

  const handleReorder = (newWidgets) => persistWidgets(newWidgets);

  const handleUpdateWidgetConfig = (widgetId, newConfig) => {
    persistWidgets(widgets.map(w => (w.id === widgetId ? { ...w, config: newConfig } : w)));
  };

  const handleAddWidget = (widgetType) => {
    // New widgets land at the end of the center column by default.
    const centerCount = widgets.filter(w => (w.column_slot || 'center') === 'center').length;
    const newWidget = { id: `temp-${Date.now()}`, widget_type: widgetType, position: centerCount, column_slot: 'center', config: {} };
    persistWidgets([...widgets, newWidget]);
  };

  const handleRemoveWidget = (widgetId) => {
    persistWidgets(widgets.filter(w => w.id !== widgetId));
  };

  const handleFollow = async () => {
    if (!profileData || !myProfile?.id) return;
    setFollowLoading(true);
    const wasFollowing = profileData.is_following;
    // Optimistic update
    setProfileData(prev => ({
      ...prev,
      is_following: !wasFollowing,
      stats: { ...prev.stats, followers: prev.stats.followers + (wasFollowing ? -1 : 1) },
    }));
    try {
      if (wasFollowing) {
        await api.unfollowUser(profileData.id, myProfile.id);
      } else {
        await api.followUser(profileData.id, myProfile.id);
      }
      await fetchProfile();
    } catch (err) {
      // Revert optimistic update + surface error
      setProfileData(prev => ({
        ...prev,
        is_following: wasFollowing,
        stats: { ...prev.stats, followers: prev.stats.followers + (wasFollowing ? 1 : -1) },
      }));
      alert(err?.response?.data?.detail || 'Could not update follow. Please try again.');
    }
    setFollowLoading(false);
  };

  const handleBlock = async () => {
    if (!profileData || !myProfile?.id) return;
    setBlockLoading(true);
    setShowBlockConfirm(false); // close dialog immediately
    try {
      await api.blockUser(profileData.id, myProfile.id);
      onBack?.(); // return to feed; backend already filters blocked users out
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not block user. Please try again.');
    }
    setBlockLoading(false);
  };

  const handleChangeWallpaper = async () => {
    try {
      const res = await api.getUserWallpapers(myProfile.id);
      setWallpapers(res.data || []);
      setShowWallpaperPicker(true);
    } catch { /* ignore */ }
  };

  const handleSelectWallpaper = async (wpId) => {
    try {
      await api.setActiveWallpaper(myProfile.id, wpId);
      setShowWallpaperPicker(false);
      await fetchProfile();
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-card rounded-xl p-8 text-center max-w-sm">
          <Lock className="w-12 h-12 text-primary/30 mx-auto mb-4" />
          <h2 className="font-heading text-lg font-semibold text-text-main mb-2">Profile Not Available</h2>
          <p className="text-sm text-text-muted mb-6">{error}</p>
          <button onClick={onBack} className="px-6 py-2 bg-primary text-white rounded-lg" data-testid="profile-go-back">Go Back</button>
        </div>
      </div>
    );
  }

  if (!profileData) return null;

  const isOwn = profileData.is_own;
  const hasCustomWallpaper = profileData.wallpaper_url && profileData.wallpaper_url !== 'default_gradient';

  const identityCardProps = {
    // Pencil / Done lives on the identity card (see IdentityCardWidget)
    onToggleEdit: isOwn && isEligible ? () => setEditMode((v) => !v) : undefined,
    profileData,
    myProfile,
    isOwn,
    followLoading,
    onNavigate,
    onFollow: handleFollow,
    onMessage: () => {
      if (myProfile?.is_premium) {
        onNavigate?.(`messages:${profileData.id}`);
      } else {
        onNavigate?.('premium');
      }
    },
    onBlock: () => setShowBlockConfirm(true),
    onUpgrade: () => onNavigate?.('premium'),
  };

  return (
    <div className="min-h-screen relative" data-testid="public-profile-page">
      {/* Block Confirm Modal */}
      {showBlockConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl" data-testid="block-confirm-modal">
            <h3 className="font-heading text-lg font-semibold text-text-main mb-2">Block {profileData.display_name}?</h3>
            <p className="text-sm text-text-muted mb-6">
              You won't see each other's posts, comments, or profiles. They won't be notified.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowBlockConfirm(false)} className="px-4 py-2 text-sm text-text-muted hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleBlock} disabled={blockLoading}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                data-testid="confirm-block-btn">{blockLoading ? 'Blocking...' : 'Block'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Wallpaper Picker Modal */}
      <AnimatePresence>
        {showWallpaperPicker && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-lg w-full mx-4 shadow-2xl max-h-[80vh] overflow-y-auto"
              data-testid="wallpaper-picker">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-semibold text-text-main">Change Wallpaper</h3>
                <button onClick={() => setShowWallpaperPicker(false)} className="p-1 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {wallpapers.filter(w => w.owned).map(wp => (
                  <button key={wp.id} onClick={() => handleSelectWallpaper(wp.id)}
                    className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                      profileData.active_wallpaper_id === wp.id ? 'border-primary ring-2 ring-primary/30' : 'border-transparent hover:border-primary/30'
                    }`} data-testid={`wallpaper-option-${wp.id}`}>
                    {wp.image_url === 'default_gradient' ? (
                      <div className="aspect-video" style={{ background: 'linear-gradient(135deg, #4AADA8 0%, #A99CC8 100%)' }} />
                    ) : (
                      <img src={wp.image_url} alt={wp.name} className="aspect-video object-cover w-full" />
                    )}
                    <p className="text-xs text-center py-1.5 font-medium text-text-main">{wp.name}</p>
                    {profileData.active_wallpaper_id === wp.id && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                ))}
                {wallpapers.filter(w => !w.owned && w.price > 0).map(wp => (
                  <button key={wp.id} onClick={() => {
                      setShowWallpaperPicker(false);
                      onNavigate?.('premium');
                    }}
                    className="relative rounded-lg overflow-hidden border-2 border-dashed border-primary/20 hover:border-primary/60 transition-colors"
                    data-testid={`wallpaper-locked-${wp.id}`}>
                    {wp.image_url === 'default_gradient' ? (
                      <div className="aspect-video bg-primary/10" />
                    ) : (
                      <img src={wp.image_url} alt={wp.name} className="aspect-video object-cover w-full opacity-60" />
                    )}
                    <p className="text-xs text-center py-1.5 font-medium text-text-muted">{wp.name}</p>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <span className="px-3 py-1 bg-primary text-white rounded-full text-xs font-medium flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Sirati+
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Wallpaper Background */}
      {hasCustomWallpaper ? (
        <div className="fixed inset-0 z-0">
          <img src={profileData.wallpaper_url} alt={`${profileData.display_name}'s profile wallpaper`} className="w-full h-full object-cover" />
        </div>
      ) : (
        <DefaultWallpaper />
      )}

      {/* Content overlay */}
      <div className="relative z-10 min-h-screen flex flex-col p-4 lg:p-8">
        {/* Back button */}
        <button onClick={onBack}
          className="absolute top-12 left-4 p-2.5 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition-colors z-20"
          data-testid="profile-back-btn">
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>

        {/* Change Wallpaper button (owner only) */}
        {isOwn && (
          <button onClick={handleChangeWallpaper}
            className="absolute top-12 right-4 px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-white text-xs font-medium hover:bg-white/30 transition-colors flex items-center gap-1.5 z-20"
            data-testid="change-wallpaper-btn">
            <Image className="w-3.5 h-3.5" /> Change Wallpaper
          </button>
        )}

        {/* Spacer pushes content toward the bottom third of the screen */}
        <div className="flex-1" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="group relative w-fit mx-auto mb-6 sm:mb-12 lg:flex lg:justify-center lg:w-full lg:max-w-5xl lg:px-4"
        >
          {isEligible ? (
            widgetsLoading ? null : widgets.length > 0 ? (
              <ProfileWidgetCanvas
                widgets={widgets}
                editMode={editMode}
                onReorder={handleReorder}
                onUpdateWidgetConfig={handleUpdateWidgetConfig}
                onAddWidget={handleAddWidget}
                onRemoveWidget={handleRemoveWidget}
                widgetProps={identityCardProps}
              />
            ) : (
              // Defensive fallback — should never happen (the backend
              // auto-seeds a default layout), but never leave the page blank.
              <IdentityCardWidget {...identityCardProps} />
            )
          ) : (
            <IdentityCardWidget {...identityCardProps} />
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PublicProfilePage;
